import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode } from "vue";
const _hoisted_1 = { class: "w-full h-screen bg-white" };
const _hoisted_2 = {
    key: 0,
    class: "w-full h-full flex items-center justify-center text-sm text-gray-700"
};
const _hoisted_3 = {
    key: 1,
    class: "w-full h-full flex items-center justify-center text-sm text-gray-700"
};
const _hoisted_4 = {
    key: 2,
    class: "w-full h-full overflow-auto"
};
const _hoisted_5 = { class: "sticky top-0 z-10 bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-between" };
const _hoisted_6 = { class: "flex items-center gap-3" };
const _hoisted_7 = ["disabled"];
const _hoisted_8 = ["disabled"];
const _hoisted_9 = ["disabled"];
const _hoisted_10 = ["href"];
const _hoisted_11 = {
    key: 0,
    class: "px-3 py-2 text-sm text-gray-600"
};
const _hoisted_12 = ["src"];
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
const minZoom = 0.8;
const maxZoom = 2.25;
const zoomStep = 0.15;
export default /*@__PURE__*/ _defineComponent({
    __name: 'MenuPdfPage',
    setup(__props) {
        const isLoading = ref(true);
        const error = ref('');
        const pdfUrl = ref('');
        const exists = ref(false);
        const canvasContainer = ref(null);
        const isRendering = ref(false);
        const zoom = ref(1);
        const userAgent = typeof navigator !== 'undefined' ? String(navigator.userAgent).toLowerCase() : '';
        const isAndroid = userAgent.includes('android');
        // iPadOS reports as "Mac" with touch points. This catches iPadOS Safari reliably.
        const isIOS = typeof navigator !== 'undefined' &&
            (/(iphone|ipad|ipod)/i.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1));
        // On iOS, the native PDF viewer inside an iframe is unreliable (often shows only the first page)
        // and can appear blurry when scaled. For mobile, we render via PDF.js canvases instead.
        const shouldRenderPdfInBrowser = isAndroid || isIOS;
        let pdfjsPromise = null;
        const getPdfjs = async () => {
            if (!pdfjsPromise) {
                pdfjsPromise = import('pdfjs-dist');
            }
            const pdfjsLib = await pdfjsPromise;
            // IMPORTANT: pdf.js expects a string URL here. Under Webpack/Vite mixed builds,
            // worker imports can end up as non-string values and crash at runtime.
            // Use a stable CDN URL (or replace with your own hosted path if desired).
            if (pdfjsLib?.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
                ;
                pdfjsLib.GlobalWorkerOptions.workerSrc =
                    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';
            }
            return pdfjsLib;
        };
        const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
        const renderPdfToCanvas = async (url) => {
            if (!canvasContainer.value)
                return;
            canvasContainer.value.innerHTML = '';
            const pdfjsLib = await getPdfjs();
            // Mobile browsers (especially iOS/Android) can have worker loading issues depending on build setup.
            // Rendering without a worker is slower, but far more reliable in embedded/app contexts.
            const loadingTask = pdfjsLib.getDocument({ url, disableWorker: true });
            const pdf = await loadingTask.promise;
            const containerWidth = canvasContainer.value.clientWidth || window.innerWidth || 360;
            // HiDPI rendering: keep layout scale for CSS sizing, but render the backing canvas
            // at devicePixelRatio so text/lines stay crisp on mobile.
            const devicePixelRatio = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
            const maxOutputScale = 3;
            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
                const page = await pdf.getPage(pageNum);
                const baseViewport = page.getViewport({ scale: 1 });
                // Fit to container width at zoom=1, then allow enlarging beyond container with zoom.
                const fitScale = containerWidth / baseViewport.width;
                const scale = fitScale * zoom.value;
                const viewport = page.getViewport({ scale });
                const canvas = document.createElement('canvas');
                // Cap output scale to avoid extremely large canvases on very high DPR devices.
                let outputScale = Math.min(devicePixelRatio, maxOutputScale);
                const maxCanvasPixels = 18000000; // ~18MP per page
                const expectedPixels = viewport.width * viewport.height * outputScale * outputScale;
                if (expectedPixels > maxCanvasPixels) {
                    outputScale = Math.max(1, Math.sqrt(maxCanvasPixels / (viewport.width * viewport.height)));
                }
                canvas.width = Math.floor(viewport.width * outputScale);
                canvas.height = Math.floor(viewport.height * outputScale);
                // CSS size controls the on-screen zoom. Using px enables horizontal scroll when zoomed in.
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;
                canvas.className = 'mb-3 border border-gray-200 shadow-sm';
                const ctx = canvas.getContext('2d');
                if (!ctx)
                    continue;
                ctx.imageSmoothingEnabled = true;
                canvasContainer.value.appendChild(canvas);
                const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;
                await page.render({ canvasContext: ctx, viewport, transform }).promise;
            }
        };
        const rerender = async () => {
            if (!pdfUrl.value || !exists.value || !shouldRenderPdfInBrowser)
                return;
            if (!canvasContainer.value)
                return;
            isRendering.value = true;
            try {
                await nextTick();
                await renderPdfToCanvas(pdfUrl.value);
            }
            finally {
                isRendering.value = false;
            }
        };
        const zoomIn = async () => {
            zoom.value = clamp(zoom.value + zoomStep, minZoom, maxZoom);
            await rerender();
        };
        const zoomOut = async () => {
            zoom.value = clamp(zoom.value - zoomStep, minZoom, maxZoom);
            await rerender();
        };
        const resetZoom = async () => {
            zoom.value = 1;
            await rerender();
        };
        const loadMenuPdf = async () => {
            error.value = '';
            isLoading.value = true;
            try {
                const host = typeof window !== 'undefined' ? window.location.host : '';
                const response = await fetch('/public/menu-pdf', {
                    cache: 'no-store',
                    headers: {
                        // Helps project detection when Host is rewritten by proxies (dev server → admin panel)
                        'X-Project-Name': host
                    }
                });
                const data = (await response.json().catch(() => null));
                if (!response.ok || !data?.success) {
                    throw new Error(data?.message || `HTTP ${response.status}`);
                }
                // Use same-origin stream endpoint (best for Android + avoids cross-origin issues)
                pdfUrl.value = data.data?.exists ? '/public/menu-pdf/file' : '';
                exists.value = Boolean(data.data?.exists);
                if (exists.value && pdfUrl.value && shouldRenderPdfInBrowser) {
                    isRendering.value = true;
                    try {
                        // Wait until the Android canvas container is actually mounted
                        await nextTick();
                        // In some browsers the ref still isn't ready on the same tick
                        if (!canvasContainer.value) {
                            await new Promise((r) => setTimeout(r, 0));
                        }
                        if (!canvasContainer.value) {
                            throw new Error('Canvas container not available');
                        }
                        await renderPdfToCanvas(pdfUrl.value);
                    }
                    finally {
                        isRendering.value = false;
                    }
                }
            }
            catch (e) {
                error.value = e?.message || 'Fehler beim Laden der Speisekarte';
            }
            finally {
                isLoading.value = false;
            }
        };
        let resizeTimer = null;
        const onResize = () => {
            if (!shouldRenderPdfInBrowser)
                return;
            if (!exists.value || !pdfUrl.value)
                return;
            if (resizeTimer)
                window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                rerender().catch(() => void 0);
            }, 120);
        };
        onMounted(async () => {
            await loadMenuPdf();
            if (typeof window !== 'undefined') {
                window.addEventListener('resize', onResize, { passive: true });
            }
        });
        onBeforeUnmount(() => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', onResize);
            }
            if (resizeTimer)
                window.clearTimeout(resizeTimer);
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (error.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(error.value), 1 /* TEXT */))
                    : (!isLoading.value && !exists.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Aktuell ist keine Speisekarte verfügbar. "))
                        : (pdfUrl.value && _unref(shouldRenderPdfInBrowser))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createElementVNode("div", _hoisted_5, [
                                    _cache[0] || (_cache[0] = _createElementVNode("div", { class: "text-sm text-gray-700 font-medium" }, "Speisekarte", -1 /* CACHED */)),
                                    _createElementVNode("div", _hoisted_6, [
                                        _createElementVNode("button", {
                                            type: "button",
                                            class: "text-sm text-gray-700 hover:text-gray-900",
                                            onClick: zoomOut,
                                            disabled: isRendering.value,
                                            title: "Zoom -"
                                        }, " − ", 8 /* PROPS */, _hoisted_7),
                                        _createElementVNode("button", {
                                            type: "button",
                                            class: "text-sm text-gray-700 hover:text-gray-900",
                                            onClick: resetZoom,
                                            disabled: isRendering.value,
                                            title: "Zoom zurücksetzen"
                                        }, _toDisplayString(Math.round(zoom.value * 100)) + "% ", 9 /* TEXT, PROPS */, _hoisted_8),
                                        _createElementVNode("button", {
                                            type: "button",
                                            class: "text-sm text-gray-700 hover:text-gray-900",
                                            onClick: zoomIn,
                                            disabled: isRendering.value,
                                            title: "Zoom +"
                                        }, " + ", 8 /* PROPS */, _hoisted_9),
                                        _createElementVNode("a", {
                                            href: pdfUrl.value,
                                            target: "_blank",
                                            rel: "noopener",
                                            class: "text-sm text-blue-600 hover:text-blue-700"
                                        }, " Öffnen / Download ", 8 /* PROPS */, _hoisted_10)
                                    ])
                                ]),
                                (isRendering.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, " Speisekarte wird geladen… "))
                                    : _createCommentVNode("v-if", true),
                                _createElementVNode("div", {
                                    ref_key: "canvasContainer",
                                    ref: canvasContainer,
                                    class: "w-full max-w-4xl mx-auto p-2"
                                }, null, 512 /* NEED_PATCH */)
                            ]))
                            : (pdfUrl.value)
                                ? (_openBlock(), _createElementBlock("iframe", {
                                    key: 3,
                                    src: pdfUrl.value,
                                    class: "w-full h-full",
                                    title: "Speisekarte PDF"
                                }, null, 8 /* PROPS */, _hoisted_12))
                                : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
